import { useState } from 'react';
import { Users, MessageSquare, TrendingUp, Mic, AlertCircle, Calendar, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePicker } from './ui/date-picker';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

interface NewDashboardProps {
  teamId: string;
  teamName?: string;
  standupData?: any[];
  teamStats?: any;
  teamMemberCount?: number;
  groupedStandups?: Array<{
    date: string;
    standups: any[];
    teamSummary?: {
      summary: string;
      keyAccomplishments: string[];
      upcomingTasks: string[];
      blockers: string[];
      overallSentiment: 'positive' | 'neutral' | 'negative';
    };
  }>;
}

const NewDashboard = ({ teamId, teamName, standupData = [], teamStats, teamMemberCount = 1, groupedStandups = [] }: NewDashboardProps) => {
  // State for filters
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>('all');

  // Current date formatting - fix timezone issue
  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const formattedDate = selectedDateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Filter standup data by selected date
  const filterStandupsByDate = (data: any[], date: string) => {
    return data.filter(standup => {
      if (!standup.created_at) return false;
      
      // Handle both date string formats (YYYY-MM-DD and full timestamp)
      let standupDateString;
      if (standup.created_at.includes('T')) {
        // Full timestamp - extract date part
        standupDateString = standup.created_at.split('T')[0];
      } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format
        standupDateString = standup.created_at;
      } else {
        // Parse and reformat other formats
        const standupDate = new Date(standup.created_at + 'T00:00:00'); // Force local parsing
        standupDateString = standupDate.getFullYear() + '-' + 
          String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(standupDate.getDate()).padStart(2, '0');
      }
      
      return standupDateString === date;
    });
  };

  // Filter standup data by team member
  const filterStandupsByTeamMember = (data: any[], teamMember: string) => {
    if (teamMember === 'all') return data;
    return data.filter(standup => standup.user_name === teamMember);
  };

  // Apply both filters
  const dateFilteredData = filterStandupsByDate(standupData, selectedDate);
  const filteredStandupData = filterStandupsByTeamMember(dateFilteredData, selectedTeamMember);

  // Get unique dates for date picker options - convert to Date objects
  const availableDates = [...new Set(standupData.map(standup => {
    if (!standup.created_at) return null;
    
    // Handle both date string formats (same logic as filtering)
    let standupDateString;
    if (standup.created_at.includes('T')) {
      // Full timestamp - extract date part
      standupDateString = standup.created_at.split('T')[0];
    } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Already in YYYY-MM-DD format
      standupDateString = standup.created_at;
    } else {
      // Parse and reformat other formats
      const standupDate = new Date(standup.created_at + 'T00:00:00'); // Force local parsing
      standupDateString = standupDate.getFullYear() + '-' + 
        String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(standupDate.getDate()).padStart(2, '0');
    }
    
    return standupDateString;
  }).filter(Boolean))].sort().reverse().map(dateString => new Date(dateString + 'T00:00:00'));

  // Get unique team members for team member filter
  const availableTeamMembers = [...new Set(dateFilteredData.map(standup => standup.user_name).filter(Boolean))].sort();

  // Process real standup data into the three-column format
  const processStandupData = () => {
    type StandupEntry = {
      name: string;
      role: string;
      content: string;
      timestamp: string;
      initial: string;
    };

    if (!filteredStandupData || filteredStandupData.length === 0) {
      // Return empty arrays if no real data
      return {
        previousWork: [],
        plansToday: [],
        blockers: []
      };
    }

    // Process real data
    const previousWork: StandupEntry[] = [];
    const plansToday: StandupEntry[] = [];
    const blockers: StandupEntry[] = [];

    filteredStandupData.forEach(standup => {
      const userInitial = standup.user_name ? standup.user_name.charAt(0).toUpperCase() : 'U';
      const baseEntry = {
        name: standup.user_name || 'Unknown User',
        role: 'Team Member',
        timestamp: standup.created_at ? new Date(standup.created_at).toLocaleDateString() : 'Recently',
        initial: userInitial
      };

      if (standup.completed_work) {
        previousWork.push({
          ...baseEntry,
          content: standup.completed_work
        });
      }

      if (standup.planned_work) {
        plansToday.push({
          ...baseEntry,
          content: standup.planned_work
        });
      }

      if (standup.blockers) {
        blockers.push({
          ...baseEntry,
          content: standup.blockers
        });
      }
    });

    return { previousWork, plansToday, blockers };
  };

  const { previousWork, plansToday, blockers } = processStandupData();

  // Calculate team mood based on sentiment values
  const calculateTeamMood = () => {
    
    if (!filteredStandupData || filteredStandupData.length === 0) {
      return {
        averageMood: 0,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'No mood data available'
      };
    }

    // Extract sentiment values (assuming they exist in the standup data)
    const sentimentValues = filteredStandupData
      .map(standup => {
        return standup.sentiment || 0;
      })
      .filter(sentiment => sentiment !== null && sentiment !== undefined);

    if (sentimentValues.length === 0) {
      return {
        averageMood: 0,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'No mood data available'
      };
    }

    const averageMood = sentimentValues.reduce((sum, sentiment) => sum + sentiment, 0) / sentimentValues.length;

    // Convert sentiment to mood display
    if (averageMood > 0.2) {
      return {
        averageMood,
        moodEmoji: '😊',
        moodText: 'Positive',
        moodColor: '#10b981',
        description: 'Team is feeling great!'
      };
    } else if (averageMood < -0.2) {
      return {
        averageMood,
        moodEmoji: '😔',
        moodText: 'Needs Support',
        moodColor: '#ef4444',
        description: 'Team could use some support'
      };
    } else {
      return {
        averageMood,
        moodEmoji: '😐',
        moodText: 'Neutral',
        moodColor: '#94a3b8',
        description: 'Team is steady'
      };
    }
  };

  const teamMood = calculateTeamMood();

  // Calculate mood trend for the last 14 days
  const calculateMoodTrend = () => {
    const today = new Date();
    const trendData = [];
    
    // Go back 14 days
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.getFullYear() + '-' + 
        String(date.getMonth() + 1).padStart(2, '0') + '-' + 
        String(date.getDate()).padStart(2, '0');
      
      // Get standups for this date
      const dateStandups = standupData.filter(standup => {
        if (!standup.created_at) return false;
        
        let standupDateString;
        if (standup.created_at.includes('T')) {
          standupDateString = standup.created_at.split('T')[0];
        } else if (standup.created_at.match(/^\d{4}-\d{2}-\d{2}$/)) {
          standupDateString = standup.created_at;
        } else {
          const standupDate = new Date(standup.created_at + 'T00:00:00');
          standupDateString = standupDate.getFullYear() + '-' + 
            String(standupDate.getMonth() + 1).padStart(2, '0') + '-' + 
            String(standupDate.getDate()).padStart(2, '0');
        }
        
        return standupDateString === dateString;
      });
      
      // Calculate average sentiment for the day
      const sentimentValues = dateStandups
        .map(standup => standup.sentiment || 0)
        .filter(sentiment => sentiment !== null && sentiment !== undefined);
      
      const avgSentiment = sentimentValues.length > 0 
        ? sentimentValues.reduce((sum, val) => sum + val, 0) / sentimentValues.length 
        : null;
      
      trendData.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        mood: avgSentiment !== null ? parseFloat((avgSentiment * 100).toFixed(1)) : null,
        responses: dateStandups.length
      });
    }
    
    return trendData;
  };

  const moodTrendData = calculateMoodTrend();

  // Get the daily summary for the selected date
  const dailySummary = groupedStandups.find(day => day.date === selectedDate)?.teamSummary;

  // Calculate completion stats based on filtered data
  const totalExpected = teamMemberCount || 1; // Use actual team member count
  const totalResponses = filteredStandupData.length;
  const completionPercentage = totalExpected > 0 ? Math.round((totalResponses / totalExpected) * 100) : 0;
  const blockersCount = blockers.length;

  // Helper function to get sentiment styling
  const getSentimentStyling = (sentiment: 'positive' | 'neutral' | 'negative') => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: 'bg-green-50',
          text: 'text-green-700',
          border: 'border-green-200',
          emoji: '😊'
        };
      case 'negative':
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200',
          emoji: '😔'
        };
      default:
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-700',
          border: 'border-gray-200',
          emoji: '😐'
        };
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-700 mb-1">{teamName ? `${teamName} Standups` : 'Daily Standup'}</h1>
          <p className="text-slate-500 text-sm">{formattedDate}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <DatePicker
            date={selectedDateObj}
            onDateChange={(date) => {
              if (date) {
                const dateString = date.getFullYear() + '-' + 
                  String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(date.getDate()).padStart(2, '0');
                setSelectedDate(dateString);
              }
            }}
            className="w-full sm:w-64 bg-white border border-slate-200"
            placeholder="Select date"
          />
          
          <Select value={selectedTeamMember} onValueChange={setSelectedTeamMember}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by team member" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All team members</SelectItem>
              {availableTeamMembers.map((teamMember) => (
                <SelectItem key={teamMember} value={teamMember}>
                  {teamMember}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="mb-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Completion Rate */}
          <div className="bg-white rounded-lg p-6 border border-slate-200">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm font-medium text-slate-700">Completion rate</span>
            </div>
            <div className="flex items-center justify-center h-32 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Completed', value: completionPercentage, color: '#06b6d4' },
                      { name: 'Not completed', value: 100 - completionPercentage, color: '#f87171' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    dataKey="value"
                  >
                    <Cell fill="#06b6d4" />
                    <Cell fill="#f87171" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute text-lg font-semibold text-slate-700">{totalResponses}</div>
            </div>
            <div className="flex items-center justify-center space-x-4 mt-4 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-cyan-400 rounded-full"></div>
                <span className="text-slate-600">Completed</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <span className="text-slate-600">Not completed</span>
              </div>
            </div>
          </div>

          {/* Team Mood Trend */}
          <div className="bg-white rounded-lg p-6 border border-slate-200">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm font-medium text-slate-700">Team mood trend</span>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={moodTrendData} margin={{ top: 5, right: 5, bottom: 20, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    stroke="#94a3b8"
                    angle={-45}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis 
                    domain={[-100, 100]}
                    ticks={[-100, 100]}
                    tick={{ fontSize: 14 }} 
                    stroke="#94a3b8"
                    width={35}
                    tickFormatter={(value) => {
                      if (value === 100) return '😁';
                      if (value === -100) return '😢';
                      return '';
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                    formatter={(value: any) => {
                      if (value === null) return ['No data', 'Mood'];
                      const mood = value > 20 ? '😊' : value < -20 ? '😔' : '😐';
                      return [`${mood} ${value}%`, 'Mood'];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="mood" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#3b82f6' }}
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs text-slate-500 text-center mt-2">
              Last 14 days
            </div>
          </div>

          {/* Team Mood */}
          <div className="bg-white rounded-lg p-6 border border-slate-200">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm font-medium text-slate-700">Team mood today</span>
            </div>
            <div className="text-center">
              <div className="mb-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-2xl mx-auto mb-2"
                  style={{ backgroundColor: teamMood.moodColor + '20', color: teamMood.moodColor }}
                >
                  {teamMood.moodEmoji}
                </div>
                <div className="text-lg font-semibold text-slate-700">{teamMood.moodText}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {totalResponses} {totalResponses === 1 ? 'response' : 'responses'}
                </div>
              </div>
              <p className="text-sm text-slate-600">
                {teamMood.description}
              </p>
              {teamMood.averageMood !== 0 && (
                <div className="mt-3 text-xs text-slate-400">
                  Score: {teamMood.averageMood.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Daily Summary Section */}
      {dailySummary && (
        <div className="mb-8">
          <div className="bg-white rounded-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-lg">✨</span>
                <h2 className="text-lg font-semibold text-slate-700">Daily Team Summary</h2>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentStyling(dailySummary.overallSentiment).bg} ${getSentimentStyling(dailySummary.overallSentiment).text} ${getSentimentStyling(dailySummary.overallSentiment).border} border`}>
                  {getSentimentStyling(dailySummary.overallSentiment).emoji} {dailySummary.overallSentiment}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                AI-generated summary
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Overview */}
              <div>
                <p className="text-slate-700 leading-relaxed">{dailySummary.summary}</p>
              </div>
              
              {/* Three columns for details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Key Accomplishments */}
                {dailySummary.keyAccomplishments.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center space-x-2">
                      <span className="text-green-600">🎉</span>
                      <span>Key Accomplishments</span>
                    </h3>
                    <ul className="space-y-2">
                      {dailySummary.keyAccomplishments.map((accomplishment, index) => (
                        <li key={index} className="text-sm text-slate-600 flex items-start space-x-2">
                          <span className="text-green-500 mt-1">•</span>
                          <span>{accomplishment}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Upcoming Tasks */}
                {dailySummary.upcomingTasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center space-x-2">
                      <span className="text-blue-600">🎯</span>
                      <span>Upcoming Tasks</span>
                    </h3>
                    <ul className="space-y-2">
                      {dailySummary.upcomingTasks.map((task, index) => (
                        <li key={index} className="text-sm text-slate-600 flex items-start space-x-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Blockers */}
                {dailySummary.blockers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center space-x-2">
                      <span className="text-red-600">🚧</span>
                      <span>Blockers & Issues</span>
                    </h3>
                    <ul className="space-y-2">
                      {dailySummary.blockers.map((blocker, index) => (
                        <li key={index} className="text-sm text-slate-600 flex items-start space-x-2">
                          <span className="text-red-500 mt-1">•</span>
                          <span>{blocker}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Show message if no summary data */}
              {dailySummary.keyAccomplishments.length === 0 && 
               dailySummary.upcomingTasks.length === 0 && 
               dailySummary.blockers.length === 0 && (
                <div className="text-center py-4 text-slate-500">
                  <p>No detailed summary available for this day</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Previous Work Day Progress */}
        <div>
          <h2 className="text-lg font-medium text-slate-700 mb-4">Previous work day progress</h2>
          <div className="space-y-3">
            {previousWork.map((entry, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {entry.initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{entry.name}</p>
                    <p className="text-xs text-slate-500 mb-2">{entry.role}</p>
                    <p className="text-sm text-slate-700">{entry.content}</p>
                    <p className="text-xs text-slate-400 mt-2">Posted {entry.timestamp}</p>
                  </div>
                </div>
              </div>
            ))}
            {previousWork.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>No previous work reported yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Plans for Today */}
        <div>
          <h2 className="text-lg font-medium text-slate-700 mb-4">Plans for today</h2>
          <div className="space-y-3">
            {plansToday.map((entry, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {entry.initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{entry.name}</p>
                    <p className="text-xs text-slate-500 mb-2">{entry.role}</p>
                    <p className="text-sm text-slate-700">{entry.content}</p>
                    <p className="text-xs text-slate-400 mt-2">Posted {entry.timestamp}</p>
                  </div>
                </div>
              </div>
            ))}
            {plansToday.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>No plans reported yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Any Blockers */}
        <div>
          <h2 className="text-lg font-medium text-slate-700 mb-4 flex items-center">
            Any blockers? <span className="ml-2">🔥</span>
          </h2>
          <div className="space-y-3">
            {blockers.map((entry, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {entry.initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{entry.name}</p>
                    <p className="text-xs text-slate-500 mb-2">{entry.role}</p>
                    <p className="text-sm text-slate-700">{entry.content}</p>
                    <p className="text-xs text-slate-400 mt-2">Posted {entry.timestamp}</p>
                  </div>
                </div>
              </div>
            ))}
            {blockers.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>No blockers reported 🎉</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDashboard;